import { z } from 'zod';
import { latSchema, lonSchema, precisionSchema, statutSchema } from './commun';

/** Nombre maximal d'étapes d'une tournée. */
export const MAX_ETAPES = 40;

/** Durées de visite par défaut en minutes (SPEC 10.3) : 90 si un contrôle est prévu, 60 sinon. */
export const DUREE_VISITE_CONTROLE_MIN = 90;
export const DUREE_VISITE_DEFAUT_MIN = 60;

/** Pause déjeuner par défaut (SPEC 10.3) : 12h00 à 13h30, 60 minutes. */
export const PAUSE_PAR_DEFAUT = { debutMin: 12 * 60, finMin: 13 * 60 + 30, dureeMin: 60 } as const;
/** Heure de départ par défaut : 08h30. */
export const HEURE_DEPART_DEFAUT_MIN = 8 * 60 + 30;

const minuteJourSchema = z.number().int().min(0).max(1439);

export const lieuTourneeSchema = z.object({
  libelle: z.string().trim().min(1, 'Libellé requis').max(120),
  lon: lonSchema,
  lat: latSchema,
});
export type LieuTournee = z.infer<typeof lieuTourneeSchema>;

export const departTourneeSchema = lieuTourneeSchema.extend({ heureMin: minuteJourSchema });
export type DepartTournee = z.infer<typeof departTourneeSchema>;

export const pauseTourneeSchema = z
  .object({
    debutMin: minuteJourSchema,
    finMin: minuteJourSchema,
    dureeMin: z.number().int().min(5).max(240),
  })
  .refine((p) => p.finMin > p.debutMin, { message: 'La fin de pause doit suivre son début' });
export type PauseTournee = z.infer<typeof pauseTourneeSchema>;

export const fenetreTourneeSchema = z
  .object({ debutMin: minuteJourSchema, finMin: minuteJourSchema })
  .refine((f) => f.finMin >= f.debutMin, { message: 'La fin de fenêtre doit suivre son début' });
export type FenetreTournee = z.infer<typeof fenetreTourneeSchema>;

export const jourSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format aaaa-mm-jj');

/** Étape en entrée : la durée est optionnelle, le serveur applique la durée par défaut. */
export const etapeEntreeSchema = z.object({
  clientId: z.number().int().positive(),
  dureeMin: z.number().int().min(5).max(480).optional(),
  fenetre: fenetreTourneeSchema.nullable().optional(),
});
export type EtapeEntree = z.infer<typeof etapeEntreeSchema>;

/** Corps de `POST /api/tournees` et `PUT /api/tournees/:id` (remplacement complet). */
export const tourneeEntreeSchema = z.object({
  titre: z.string().trim().min(1, 'Titre requis').max(80),
  jour: jourSchema,
  depart: departTourneeSchema,
  retour: lieuTourneeSchema,
  pause: pauseTourneeSchema.nullable(),
  etapes: z.array(etapeEntreeSchema).max(MAX_ETAPES),
});
export type TourneeEntree = z.infer<typeof tourneeEntreeSchema>;

/**
 * Corps de `PUT /api/tournees/:id`. `majLe` : version sur laquelle repose la modification ; si la
 * tournée a changé depuis (ajout depuis une fiche, autre onglet), le serveur répond 409 au lieu
 * d'écraser ce changement. Absent : remplacement sans contrôle.
 */
export const tourneeModificationSchema = tourneeEntreeSchema.extend({
  majLe: z.iso.datetime().optional(),
});
export type TourneeModification = z.infer<typeof tourneeModificationSchema>;

export const tourneeIdSchema = z.object({ id: z.uuid() });

export const sourceItineraireSchema = z.enum(['ign', 'estimation']);

export const clientEtapeSchema = z.object({
  raisonSociale: z.string(),
  ville: z.string().nullable(),
  lon: z.number(),
  lat: z.number(),
  precision: precisionSchema,
  statut: statutSchema.nullable(),
});
export type ClientEtape = z.infer<typeof clientEtapeSchema>;

export const horaireEtapeSchema = z.object({
  clientId: z.number().int(),
  arriveeMin: z.number(),
  debutMin: z.number(),
  finMin: z.number(),
  attenteMin: z.number(),
  trajetMin: z.number(),
});
export type HoraireEtapeDto = z.infer<typeof horaireEtapeSchema>;

export const nonPlaceeSchema = z.object({
  clientId: z.number().int(),
  motif: z.enum(['fenetre_depassee']),
});
export type NonPlaceeDto = z.infer<typeof nonPlaceeSchema>;

export const resultatTourneeSchema = z.object({
  source: sourceItineraireSchema,
  distanceM: z.number(),
  routeMin: z.number(),
  retourMin: z.number(),
  horaires: z.array(horaireEtapeSchema),
  pause: z
    .object({
      debutMin: z.number(),
      finMin: z.number(),
      apresClientId: z.number().int().nullable(),
      tardive: z.boolean(),
    })
    .nullable(),
  nonPlacees: z.array(nonPlaceeSchema),
  trace: z
    .object({ type: z.literal('LineString'), coordinates: z.array(z.tuple([z.number(), z.number()])) })
    .nullable(),
  calculeLe: z.string(),
});
export type ResultatTournee = z.infer<typeof resultatTourneeSchema>;

export const etapeSortieSchema = z.object({
  clientId: z.number().int(),
  dureeMin: z.number().int(),
  fenetre: fenetreTourneeSchema.nullable(),
  /** Null quand le client n'est plus visible ou n'a plus de point. */
  client: clientEtapeSchema.nullable(),
});
export type EtapeSortie = z.infer<typeof etapeSortieSchema>;

export const tourneeSchema = z.object({
  id: z.uuid(),
  titre: z.string(),
  jour: jourSchema,
  depart: departTourneeSchema,
  retour: lieuTourneeSchema,
  pause: pauseTourneeSchema.nullable(),
  etapes: z.array(etapeSortieSchema),
  resultat: resultatTourneeSchema.nullable(),
  majLe: z.string(),
});
export type TourneeDto = z.infer<typeof tourneeSchema>;

export const resumeTourneeSchema = z.object({
  id: z.uuid(),
  titre: z.string(),
  jour: jourSchema,
  nbEtapes: z.number().int(),
  retourMin: z.number().nullable(),
  source: sourceItineraireSchema.nullable(),
  majLe: z.string(),
  /** Clients des étapes, pour signaler dans les menus d'ajout ceux qui y sont déjà. */
  clientIds: z.array(z.number().int()).optional(),
});
export type ResumeTournee = z.infer<typeof resumeTourneeSchema>;

export const tourneesListeSchema = z.object({ tournees: z.array(resumeTourneeSchema) });

const syntheseSchema = z.object({
  nbPlacees: z.number().int(),
  routeMin: z.number(),
  retourMin: z.number(),
});

/**
 * Origine des durées de trajet utilisées pour choisir l'ordre. `sans_objet` : moins de deux lieux,
 * rien à calculer (ce n'est pas une panne). `reelles` : toutes viennent de l'IGN. `partielles` :
 * `nbEstimees` paires sur `nbPaires` sont estimées (plafond de requêtes ou échecs). `estimees` :
 * aucune durée réelle, l'IGN est indisponible.
 */
export const originesDureesSchema = z.object({
  etat: z.enum(['sans_objet', 'reelles', 'partielles', 'estimees']),
  nbPaires: z.number().int(),
  nbEstimees: z.number().int(),
});
export type OriginesDurees = z.infer<typeof originesDureesSchema>;

/** Réponse de `POST /api/tournees/:id/optimiser` : rien n'est appliqué tant que le front ne fait pas de PUT. */
export const optimisationSchema = z.object({
  /** Identifiants de clients dans l'ordre proposé (placés d'abord, puis non placés). */
  ordre: z.array(z.number().int()),
  methode: z.enum(['exacte', 'heuristique', 'vide']),
  avant: syntheseSchema,
  apres: syntheseSchema,
  /** Minutes gagnées sur l'heure de retour, null si le nombre d'étapes placées diffère. */
  gainMin: z.number().nullable(),
  source: sourceItineraireSchema,
  /** Origine des durées qui ont servi à choisir l'ordre. */
  durees: originesDureesSchema.optional(),
  horaires: z.array(horaireEtapeSchema),
  nonPlacees: z.array(nonPlaceeSchema),
});
export type Optimisation = z.infer<typeof optimisationSchema>;

export const suggestionSchema = z.object({
  clientId: z.number().int(),
  raisonSociale: z.string(),
  ville: z.string().nullable(),
  lon: z.number(),
  lat: z.number(),
  precision: precisionSchema,
  statut: statutSchema.nullable(),
  enRetard: z.boolean(),
  prochainControle: z.string().nullable(),
  distanceTraceM: z.number(),
});
export type Suggestion = z.infer<typeof suggestionSchema>;

/** Largeur du couloir des suggestions, en km : bornes du curseur et valeur par défaut (SPEC 10.3). */
export const COULOIR_KM_MIN = 1;
export const COULOIR_KM_MAX = 30;
export const COULOIR_KM_DEFAUT = 5;
/** Distance maximale par défaut entre un client suggéré et la trace, en mètres. */
export const COULOIR_M = COULOIR_KM_DEFAUT * 1000;
/** Nombre maximal de suggestions renvoyées. */
export const LIMITE_SUGGESTIONS = 30;

function enBooleen(valeur: unknown): unknown {
  if (typeof valeur === 'string') return valeur === 'true' || valeur === '1';
  return valeur;
}

/**
 * Paramètres de `GET /api/tournees/:id/suggestions`. Sans paramètre, le comportement historique :
 * 5 km, clients actifs (ou sans statut, comme sur la carte), contrôles en retard d'abord.
 */
export const suggestionsRequeteSchema = z.object({
  rayon_km: z.coerce.number().min(COULOIR_KM_MIN).max(COULOIR_KM_MAX).default(COULOIR_KM_DEFAUT),
  actifs: z.preprocess(enBooleen, z.boolean()).default(true),
  retard: z.preprocess(enBooleen, z.boolean()).default(false),
  /** `priorite` : en retard, puis prochain contrôle, puis distance. `distance` : plus proches d'abord. */
  tri: z.enum(['priorite', 'distance']).default('priorite'),
});
export type SuggestionsRequete = z.infer<typeof suggestionsRequeteSchema>;

/** Les champs ajoutés ont une valeur par défaut : une réponse de l'ancienne forme reste lisible. */
export const suggestionsSchema = z.object({
  suggestions: z.array(suggestionSchema),
  /** Largeur de couloir appliquée, en km. */
  rayonKm: z.number().default(COULOIR_KM_DEFAUT),
  /**
   * `trace` : autour du tracé calculé. `points` : autour du départ, du retour et des étapes
   * localisées, quand il n'y a pas encore de tracé (tournée sans étape placée).
   */
  couloir: z.enum(['trace', 'points']).default('trace'),
  /** Vrai quand plus de `LIMITE_SUGGESTIONS` clients correspondent : seuls les premiers sont renvoyés. */
  tronque: z.boolean().default(false),
});
export type Suggestions = z.infer<typeof suggestionsSchema>;

/**
 * Corps de `POST /api/tournees/:id/etapes` : ajoute des étapes sans réécrire la tournée.
 * Les clients déjà présents sont ignorés (ajout idempotent). Sans `position`, ajout en fin.
 */
export const ajoutEtapesSchema = z.object({
  etapes: z.array(etapeEntreeSchema).min(1).max(MAX_ETAPES),
  /** Index d'insertion (0 = en tête). Au-delà du nombre d'étapes, ajout en fin. */
  position: z.number().int().min(0).optional(),
});
export type AjoutEtapes = z.infer<typeof ajoutEtapesSchema>;

export const ajoutEtapesReponseSchema = z.object({
  tournee: tourneeSchema,
  /** Clients réellement ajoutés, dans l'ordre d'insertion. */
  ajoutes: z.array(z.number().int()),
  /** Clients demandés mais déjà dans la tournée. */
  dejaPresents: z.array(z.number().int()),
});
export type AjoutEtapesReponse = z.infer<typeof ajoutEtapesReponseSchema>;
